import useTranslation from 'next-translate/useTranslation';

import { formatDollars } from '~/domains/common/utils/formatters';
import { Text } from '~/domains/design-system/Text';

import useStyles from './BalanceUpdate.styles';
import type { BalanceUpdateProps } from './types';

function BalanceUpdate({
  previousBalance,
  newBalance,
  balanceType,
  variant = 'light',
  layout = 'default',
  showBorder = true,
  showBottomDivider = false,
}: BalanceUpdateProps) {
  const { t } = useTranslation('common');
  const { classes } = useStyles({ variant, layout, showBorder, showBottomDivider });

  // Don't render if balance values are not provided
  if (typeof previousBalance !== 'number' || typeof newBalance !== 'number') {
    return null;
  }

  // For default layout: title is "Cash Balance" or "Credits", second line starts with "Balance:"
  // For compact layout: single line "Cash Balance:" or "Credits Balance:" followed by values
  const getTitle = () => {
    if (layout === 'compact') {
      return balanceType === 'credits'
        ? `${t('balanceUpdate.creditsBalance')}:`
        : `${t('balanceUpdate.cashBalance')}:`;
    }
    return balanceType === 'credits' ? t('balanceUpdate.credits') : t('balanceUpdate.cash');
  };

  const title = getTitle();

  const titleVariant = layout === 'compact' ? 'title-medium' : 'subhead-medium';

  return (
    <div className={classes.container}>
      <Text variant={titleVariant} className={classes.title}>
        {title}
      </Text>
      <div className={classes.balanceRow}>
        {layout === 'default' && (
          <Text variant="title-medium" className={classes.label}>
            {t('balanceUpdate.balance')}:
          </Text>
        )}
        <Text variant="title-medium" className={classes.previousBalance}>
          {formatDollars(previousBalance, true)}
        </Text>
        <Text variant="title-medium" className={classes.arrow}>
          →
        </Text>
        <Text variant="title-medium" className={classes.newBalance}>
          {formatDollars(newBalance, true)}
        </Text>
      </div>
    </div>
  );
}

export default BalanceUpdate;
